/* Native section lifecycle: no saved product/social JSON, no prototype destinations. */
(() => {
  if (customElements.get('sitc-home-section')) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  class SITCSection extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.cleanups = [];
      this.on = (element, event, callback, options = {}) => element?.addEventListener(event, callback, { ...options, signal: this.abort.signal });
      this.modals = [...this.querySelectorAll('quick-view-modal')];
      // Keep native commerce overlays out of the homepage's typography and layout scope.
      this.modals.forEach(modal => document.body.append(modal));
      this.setupTabs();
      this.setupBrands();
      this.setupRails();
      this.setupQuotes();
      this.setupMarquee();
      this.on(this, 'click', event => {
        const variant = event.target.closest('[data-gift-variant]');
        if (!variant) return;
        this.querySelectorAll('[data-gift-variant]').forEach(b => b.setAttribute('aria-pressed', String(b === variant)));
        this.querySelector('.gift-cta').href = variant.dataset.giftUrl;
      });
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.cleanups?.forEach(clean => clean());
      this.modals?.forEach(modal => modal.remove());
    }
    setupTabs() {
      const list = this.querySelector('[role=tablist]');
      if (!list) return;
      const tabs = [...list.querySelectorAll('[role=tab]')];
      const select = tab => {
        tabs.forEach(t => {
          const active = t === tab;
          t.setAttribute('aria-selected', String(active));
          t.tabIndex = active ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !active;
        });
        const heading = this.querySelector('[data-shelf-heading]');
        const description = this.querySelector('[data-shelf-description]');
        if (heading) heading.textContent = tab.dataset.heading;
        if (description) description.textContent = tab.dataset.description;
        this.dispatchEvent(new CustomEvent('sitc:shelfchange'));
      };
      this.on(list, 'click', event => { const tab = event.target.closest('[role=tab]'); if (tab) select(tab); });
      this.on(list, 'keydown', event => {
        const index = tabs.indexOf(document.activeElement);
        if (index < 0) return;
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault(); select(tabs[next]); tabs[next].focus();
      });
      this.on(document, 'shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const panel = event.target.closest('[role=tabpanel]');
        const tab = panel && tabs.find(t => t.getAttribute('aria-controls') === panel.id);
        if (tab) select(tab);
      });
    }
    setupBrands() {
      const form = this.querySelector('.brand-search-form');
      if (!form) return;
      const input = form.querySelector('input');
      const clear = form.querySelector('.clear-brand-search');
      const normal = this.querySelector('.brand-default');
      const matches = this.querySelector('.brand-matches');
      const links = [...this.querySelectorAll('[data-brand-name]')];
      const normalize = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
      const update = () => {
        const value = normalize(input.value);
        if (normal) normal.hidden = Boolean(value);
        if (matches) matches.hidden = !value;
        if (clear) clear.hidden = !value;
        let count = 0;
        links.forEach(link => { link.hidden = !normalize(link.dataset.brandName).includes(value); if (!link.hidden) count++; });
        this.classList.toggle('is-brand-searching', Boolean(value));
        this.classList.toggle('is-brand-empty', Boolean(value) && count === 0);
        const resultList = matches?.querySelector('.homepage-brand-results');
        if (resultList) resultList.hidden = count === 0;
        const empty = this.querySelector('[data-no-brands]');
        if (empty) empty.hidden = count > 0;
        const status = form.querySelector('[aria-live]');
        if (status) status.textContent = value ? this.dataset.resultsLabel.replace('[count]', count) : '';
      };
      this.on(form, 'submit', event => { event.preventDefault(); update(); });
      this.on(input, 'input', update);
      this.on(clear, 'click', () => { input.value = ''; update(); input.focus(); });
      update();
    }
    setupRails() {
      this.querySelectorAll('.mobile-rail-controls').forEach(controls => {
        const rail = controls.previousElementSibling;
        const prev = controls.querySelector('[data-products-prev]');
        const next = controls.querySelector('[data-products-next]');
        const update = () => { prev.disabled = rail.scrollLeft < 2; next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2; };
        this.on(prev, 'click', () => rail.scrollBy({ left: -rail.clientWidth * .9, behavior: reduce.matches ? 'instant' : 'smooth' }));
        this.on(next, 'click', () => rail.scrollBy({ left: rail.clientWidth * .9, behavior: reduce.matches ? 'instant' : 'smooth' }));
        this.on(rail, 'scroll', update, { passive: true });
        this.on(this, 'sitc:shelfchange', () => requestAnimationFrame(update));
        const resize = new ResizeObserver(update); resize.observe(rail); this.cleanups.push(() => resize.disconnect()); update();
      });
    }
    setupQuotes() {
      const quotes = [...this.querySelectorAll('.customer-quote')];
      if (!quotes.length) return;
      let index = 0;
      const show = next => {
        index = (next + quotes.length) % quotes.length;
        quotes.forEach((quote, i) => { quote.hidden = i !== index; });
        const count = this.querySelector('[data-quote-count]');
        if (count) count.textContent = `${index + 1} / ${quotes.length}`;
      };
      this.on(this.querySelector('[data-quote-prev]'), 'click', () => show(index - 1));
      this.on(this.querySelector('[data-quote-next]'), 'click', () => show(index + 1));
      this.on(document, 'shopify:block:select', event => { const quote = event.target.closest('.customer-quote'); const i = quotes.indexOf(quote); if (i >= 0) show(i); });
    }
    setupMarquee() {
      const rail = this.querySelector('.brand-runway');
      if (!rail) return;
      const sequence = rail.querySelector('.brand-sequence');
      const links = [...sequence.querySelectorAll('.marquee-brand')];
      const toggle = this.querySelector('[data-brand-motion]');
      const desktop = matchMedia('(min-width:1024px) and (hover:hover) and (pointer:fine)');
      const repeat = sequence.cloneNode(true);
      repeat.classList.add('brand-repeat');
      repeat.setAttribute('aria-hidden', 'true');
      repeat.querySelectorAll('*').forEach(element => {
        element.removeAttribute('id');
        element.removeAttribute('data-shopify-editor-block');
        if (element.matches('a')) element.tabIndex = -1;
      });
      repeat.hidden = true;
      sequence.after(repeat);
      // The visual repeat keeps mouse shopping links, but never enters keyboard/AT order.
      this.on(repeat, 'pointerdown', event => { if (event.pointerType === 'mouse') event.preventDefault(); });
      let frame = 0, last = 0, position = 0, period = 0, manualEnd = 0;
      let enabled = false, inView = false, hovered = false, focused = false, paused = false, drag = null;
      const stop = () => { cancelAnimationFrame(frame); frame = 0; last = 0; };
      const canRun = () => enabled && inView && !document.hidden && !hovered && !focused && !paused && !drag;
      const tick = time => {
        frame = 0;
        if (!canRun()) return;
        if (last) position = (position + Math.min(time - last, 64) * .018) % period;
        last = time;
        rail.scrollLeft = position;
        frame = requestAnimationFrame(tick);
      };
      const sync = () => {
        stop();
        if (canRun()) { position = rail.scrollLeft % period; frame = requestAnimationFrame(tick); }
      };
      const measure = () => {
        const trackStyle = getComputedStyle(sequence.parentElement);
        period = sequence.offsetWidth + parseFloat(trackStyle.columnGap || 0);
        manualEnd = Math.max(0, sequence.offsetWidth + parseFloat(trackStyle.paddingLeft || 0) + parseFloat(trackStyle.paddingRight || 0) - rail.clientWidth);
        enabled = desktop.matches && !reduce.matches && !window.Shopify?.designMode && period > rail.clientWidth;
        repeat.hidden = !enabled;
        toggle.hidden = !enabled;
        sync();
      };
      this.on(toggle, 'click', () => {
        paused = !paused;
        toggle.textContent = paused ? this.dataset.resumeLabel : this.dataset.pauseLabel;
        sync();
      });
      this.on(this, 'pointerenter', event => { if (event.pointerType === 'mouse') { hovered = true; sync(); } });
      this.on(this, 'pointerleave', () => { hovered = false; sync(); });
      this.on(this, 'focusin', () => { focused = true; sync(); });
      this.on(this, 'focusout', event => { focused = this.contains(event.relatedTarget); sync(); });
      this.on(document, 'visibilitychange', sync);
      this.on(desktop, 'change', measure);
      this.on(reduce, 'change', measure);
      const move = direction => rail.scrollBy({left:direction * rail.clientWidth * .7, behavior:reduce.matches ? 'instant' : 'smooth'});
      this.on(rail, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.target !== rail) {
          const index = links.indexOf(event.target.closest('.marquee-brand'));
          const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? links.length - 1 :
            Math.max(0, Math.min(links.length - 1, index + (event.key === 'ArrowRight' ? 1 : -1)));
          links[nextIndex]?.focus({preventScroll:true});
          links[nextIndex]?.scrollIntoView({block:'nearest', inline:'nearest', behavior:'instant'});
        } else if (event.key === 'Home' || event.key === 'End') {
          rail.scrollTo({left:event.key === 'Home' ? 0 : manualEnd, behavior:'instant'});
        } else move(event.key === 'ArrowRight' ? 1 : -1);
      });
      this.on(rail, 'focusin', event => event.target.closest('.marquee-brand')?.scrollIntoView({block:'nearest', inline:'nearest', behavior:'instant'}));
      // Touch uses native scrolling; mouse dragging adds a generous manual affordance.
      this.on(rail, 'dragstart', event => event.preventDefault());
      this.on(rail, 'pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        suppressClick = false;
        drag = {x:event.clientX, left:rail.scrollLeft, moved:false}; sync();
      });
      this.on(window, 'pointermove', event => {
        if (!drag) return;
        if (Math.abs(event.clientX - drag.x) > 6) drag.moved = true;
        if (!drag.moved) return;
        rail.setAttribute('data-dragging', '');
        rail.scrollLeft = drag.left + drag.x - event.clientX;
      }, {passive:true});
      let suppressClick = false;
      const endDrag = () => {
        if (!drag) return;
        suppressClick = drag.moved; drag = null; rail.removeAttribute('data-dragging'); sync();
      };
      this.on(window, 'pointerup', endDrag);
      this.on(window, 'pointercancel', endDrag);
      this.on(rail, 'click', event => {
        if (suppressClick) { event.preventDefault(); event.stopPropagation(); suppressClick = false; }
      }, {capture:true});
      const resize = new ResizeObserver(measure);
      resize.observe(rail); resize.observe(sequence);
      const visibility = new IntersectionObserver(entries => { inView = entries[0].isIntersecting; sync(); });
      visibility.observe(rail);
      this.cleanups.push(() => { stop(); resize.disconnect(); visibility.disconnect(); repeat.remove(); });
      measure();
    }
  }
  customElements.define('sitc-home-section', SITCSection);
})();

/* Theme-local compatibility for the existing Reviews tab. Native app clicks still own opening/closing. */
(() => {
  let trigger, drawer, open = false, returnFocus, scheduled = false;
  const visible = element => element && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden';
  const update = () => {
    scheduled = false;
    const launchers = [...document.querySelectorAll('.yotpo-reviews-tab-floating-tab')];
    trigger = launchers.find(visible) || launchers[0];
    drawer = document.querySelector('.reviews-tab-drawer-container');
    if (!trigger) return;
    const next = Boolean(drawer?.classList.contains('reviews-tab-drawer-container-visible'));
    trigger.setAttribute('role', 'button'); trigger.tabIndex = 0;
    trigger.setAttribute('aria-label', next ? 'Close customer reviews' : 'Open customer reviews');
    trigger.setAttribute('aria-expanded', String(next));
    if (drawer) {
      drawer.setAttribute('role', 'dialog'); drawer.setAttribute('aria-label', 'Customer reviews'); drawer.tabIndex = -1;
      drawer.setAttribute('aria-hidden', String(!next));
      if (next) drawer.setAttribute('aria-modal', 'true'); else drawer.removeAttribute('aria-modal');
    }
    if (next !== open) {
      open = next;
      if (open) { returnFocus = document.activeElement; drawer.focus({preventScroll:true}); }
      else if (returnFocus?.isConnected) trigger.focus({preventScroll:true});
    }
  };
  new MutationObserver(() => { if (!scheduled) { scheduled = true; requestAnimationFrame(update); } })
    .observe(document.body, {childList:true, subtree:true, attributes:true, attributeFilter:['class']});
  document.addEventListener('keydown', event => {
    if (event.target === trigger && ['Enter', ' '].includes(event.key)) { event.preventDefault(); trigger.click(); return; }
    if (!open) return;
    if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); trigger.click(); return; }
    if (event.key !== 'Tab') return;
    const stops = [trigger, ...drawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex="0"]')].filter(visible);
    const first = stops[0], last = stops.at(-1);
    if (!first) { event.preventDefault(); drawer.focus(); return; }
    if (event.shiftKey && (document.activeElement === first || document.activeElement === drawer || !drawer.contains(document.activeElement) && document.activeElement !== trigger)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || document.activeElement === drawer || !drawer.contains(document.activeElement) && document.activeElement !== trigger)) { event.preventDefault(); first.focus(); }
  }, true);
  update();
})();
